import { useMemo, useState } from "react";
import { Box, CircularProgress, Radio, Typography } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { Layers } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BUTTON_ANALYZE,
  BUTTON_TRY_AGAIN,
  MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_EMPTY_DESCRIPTION,
  MODAL_IMAGE_EDIT_LAYERS_ERROR_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_ERROR_DESCRIPTION,
  MODAL_IMAGE_EDIT_LAYERS_ADD,
  MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_BACKGROUND,
  MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME,
  MODAL_IMAGE_EDIT_LAYERS_ANALYZING,
} from "../../../../i18n/keysTranslations";
import Button from "../../../Buttons/Button";
import UploadFileButton from "../../../Buttons/UploadFileButton";
import { IMAGE_FORMATS } from "../../../../utils/constants";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { LayerNameEditor } from "../shared";

const SELECTED_BORDER = "#111";

const SortableLayerRow = ({
  layer,
  selected,
  onSelect,
  onRename,
  backgroundLabel,
  fallbackName,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });
  const name =
    layer.name || (layer.isBackground ? backgroundLabel : fallbackName);

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onClick={() => onSelect(layer.id)}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.875,
        border: selected
          ? `1.5px solid ${SELECTED_BORDER}`
          : "1px solid rgba(0,0,0,0.08)",
        borderRadius: 2,
        bgcolor: "#fff",
        cursor: "pointer",
        textAlign: "left",
        opacity: isDragging ? 0.7 : 1,
        boxShadow: isDragging ? "0 8px 20px rgba(17, 24, 39, 0.12)" : "none",
        zIndex: isDragging ? 2 : 1,
        position: "relative",
      }}
    >
      <Box
        component="span"
        {...attributes}
        {...listeners}
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          color: "rgba(0,0,0,0.28)",
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
          flexShrink: 0,
        }}
      >
        <DragIndicator fontSize="small" />
      </Box>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "grey.100",
          flexShrink: 0,
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {layer.url ? (
          <Box
            component="img"
            src={layer.url}
            alt={name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <LayerNameEditor
          name={name}
          onStartEdit={() => onSelect(layer.id)}
          onCommit={(nextName) => {
            const trimmed = String(nextName || "").trim();
            if (trimmed === name && (!layer.name || layer.name === trimmed)) {
              return;
            }
            onRename(layer.id, trimmed);
          }}
        />
      </Box>
      <Radio
        checked={selected}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onSelect(layer.id)}
        size="small"
        sx={{
          p: 0.5,
          color: "rgba(0,0,0,0.22)",
          "&.Mui-checked": { color: SELECTED_BORDER },
        }}
      />
    </Box>
  );
};

const LayersSidebar = ({
  layers = [],
  selectedLayerId,
  onSelectLayer,
  isAnalyzing,
  errorMessage,
  onAnalyze,
  onAddLayerUrls,
  onReorderLayers = () => {},
  onRenameLayer = () => {},
  isLoadingSnapshot = false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const [isAddingLayer, setIsAddingLayer] = useState(false);
  const hasLayers = layers.length > 0;

  const onSelectLayerFiles = (files) => {
    if (files?.length) {
      setIsAddingLayer(true);
    }
  };

  const onUploadedLayerUrls = async (urls) => {
    const urlList = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
    if (!urlList.length || typeof onAddLayerUrls !== "function") {
      return;
    }
    await onAddLayerUrls(urlList);
  };

  const listLayers = useMemo(() => [...layers].reverse(), [layers]);
  const sortableIds = useMemo(
    () => listLayers.map((layer) => layer.id),
    [listLayers]
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortableIds.indexOf(active.id);
    const newIndex = sortableIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    onReorderLayers(arrayMove(sortableIds, oldIndex, newIndex));
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          py: 2,
          minHeight: 0,
        }}
      >
        {hasLayers ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.secondary",
                }}
              >
                {t(MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE)}
              </Typography>
              <Box
                component="label"
                htmlFor={isAddingLayer ? undefined : "layer-import-images"}
                aria-busy={isAddingLayer}
                sx={{
                  color: "#111",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: isAddingLayer ? "default" : "pointer",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  minWidth: 16,
                  minHeight: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  pointerEvents: isAddingLayer ? "none" : "auto",
                }}
              >
                {isAddingLayer ? (
                  <CircularProgress size={14} sx={{ color: "#111" }} />
                ) : (
                  `+ ${t(MODAL_IMAGE_EDIT_LAYERS_ADD)}`
                )}
              </Box>
              <UploadFileButton
                id="layer-import-images"
                acceptedFormats={IMAGE_FORMATS}
                multiple={true}
                pathStorage={`socialMedia/${userID}/`}
                textUpload={MODAL_IMAGE_EDIT_LAYERS_ADD}
                colorUpload="secondary"
                isHidden={true}
                disabled={isAddingLayer}
                onFileSelect={onSelectLayerFiles}
                onChange={onUploadedLayerUrls}
                onUploadSettled={() => setIsAddingLayer(false)}
              />
            </Box>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                {listLayers.map((layer, index) => (
                  <SortableLayerRow
                    key={layer.id}
                    layer={layer}
                    selected={selectedLayerId === layer.id}
                    onSelect={onSelectLayer}
                    onRename={onRenameLayer}
                    backgroundLabel={t(MODAL_IMAGE_EDIT_LAYERS_BACKGROUND)}
                    fallbackName={t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
                      index: listLayers.length - index,
                    })}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Box sx={{ textAlign: "center", maxWidth: 320 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "#000",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Layers size={24} color="#fff" />
                </Box>
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#000", mb: 0.5 }}
              >
                {errorMessage
                  ? t(MODAL_IMAGE_EDIT_LAYERS_ERROR_TITLE)
                  : t(MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {errorMessage
                  ? t(MODAL_IMAGE_EDIT_LAYERS_ERROR_DESCRIPTION)
                  : isAnalyzing
                    ? t(MODAL_IMAGE_EDIT_LAYERS_ANALYZING)
                    : t(MODAL_IMAGE_EDIT_LAYERS_EMPTY_DESCRIPTION)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {hasLayers ? null : (
        <Box sx={{ px: 2, py: 2, flexShrink: 0 }}>
          <Button
            color="primary"
            fullWidth
            disabled={isAnalyzing || isLoadingSnapshot || !onAnalyze}
            onClick={onAnalyze}
          >
            {errorMessage ? t(BUTTON_TRY_AGAIN) : t(BUTTON_ANALYZE)}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LayersSidebar;
